import { Container, Row, Col, Accordion } from 'react-bootstrap';
import {
  VideoCamera,
  ClockCounterClockwise,
  AppleLogo,
  GooglePlayLogo,
  ChatCircleText,
  UsersThree,
  Calendar,
  EnvelopeOpen,
  CaretDown
} from 'phosphor-react';
import { Helmet } from 'react-helmet-async';
import '../css/HomePage.css';
import { useStoreLinks } from '../lib/useStoreLinks';

const APP_STORE_URL =
  'https://apps.apple.com/app/apple-store/id6504144806?pt=126766867&ct=SEO-Home-Hero&mt=8';

const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.famring.membox&referrer=utm_source%3Dfamilyring.io%26utm_medium%3Dweb%26utm_campaign%3DSEO-Home-Hero';

function HomePage() {
  // Uses the ad's campaign code when the visitor came from an ad.
  const storeLinks = useStoreLinks();
  const hero = storeLinks(APP_STORE_URL, PLAY_STORE_URL);
  const closing = storeLinks(
    APP_STORE_URL.replace('SEO-Home-Hero', 'SEO-Home-Closing'),
    PLAY_STORE_URL.replace('SEO-Home-Hero', 'SEO-Home-Closing')
  );

  // FAQ data shared between visible UI and FAQPage schema.
  const faqs = [
    {
      q: 'What is Family Ring?',
      a: "Family Ring is a private mobile app for capturing your family's stories and legacy. It combines guided video interviews with your parents and grandparents, weekly video check-ins across the whole family, and shared celebrations — all on one subscription that covers up to 10 family members."
    },
    {
      q: 'Is there a free trial?',
      a: "Yes — the first six months are free with full access to every feature. After the trial it's $4.99/month for your whole family (up to 10 family members) on one subscription. You can cancel anytime."
    },
    {
      q: 'How is this different from StoryWorth?',
      a: "StoryWorth sends one written question per week by email to one person and compiles their answers into a hardcover book at year's end. Family Ring is video-first with AI-enhanced questions and captions. It includes your whole family on one subscription — not just one storyteller — and is only $4.99/month for the whole family after a 6-month free trial."
    },
    {
      q: 'Can I download my videos?',
      a: "Yes. You can download your videos anytime — during your trial, while subscribed, or after canceling. Your family's stories are yours; we don't lock them up."
    },
    {
      q: 'Is Family Ring private?',
      a: "Yes. It's invite-only. There's no public feed, no discoverability, no algorithm. Only people you invite into your family ring can see your stories."
    },
    {
      q: "Is Family Ring the jewelry brand?",
      a: 'No — different thing. Family Ring is a mobile app for private family video stories. You can download it from the major app stores and use it from the web.'
    }
  ];

  // JSON-LD schema payloads for both SoftwareApplication and FAQPage.
  const softwareAppSchema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Family Ring',
    alternateName: 'Family Ring App',
    applicationCategory: 'LifestyleApplication',
    operatingSystem: 'iOS, Android',
    description:
      "The modern way to capture your family's stories and legacy. Private mobile app for guided video interviews with parents and grandparents, weekly family video moments, and shared celebrations. One subscription covers up to 10 family members. 6 months free.",
    offers: {
      '@type': 'Offer',
      price: '4.99',
      priceCurrency: 'USD',
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: '4.99',
        priceCurrency: 'USD',
        unitCode: 'MON',
        referenceQuantity: {
          '@type': 'QuantitativeValue',
          value: '1',
          unitCode: 'MON'
        }
      }
    },
    publisher: {
      '@type': 'Organization',
      name: 'Howell Co',
      url: 'https://familyring.io'
    },
    url: 'https://familyring.io',
    downloadUrl:
      'https://apps.apple.com/us/app/family-ring/id6504144806'
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: a
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>Family Ring App — Modern Family Story & Legacy App</title>
        <meta
          name="description"
          content="The modern way to capture your family's stories and legacy. Guided video interviews with your parents and grandparents. Free for 6 months, then $4.99/month for the whole family. Download free."
        />
        <link rel="canonical" href="https://familyring.io/" />
        <meta property="og:title" content="Family Ring App — Modern Family Story & Legacy App" />
        <meta
          property="og:description"
          content="The modern way to capture your family's stories and legacy. Free for 6 months. Then $4.99/month for the whole family."
        />
        <meta property="og:url" content="https://familyring.io/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://familyring.io/brand/og-home.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Family Ring App — Modern Family Story & Legacy App" />
        <meta
          name="twitter:description"
          content="The modern way to capture your family's stories and legacy."
        />
        <meta name="twitter:image" content="https://familyring.io/brand/og-home.png" />
        <script type="application/ld+json">{JSON.stringify(softwareAppSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="hero">
        <img
          src="/brand/hero-banner.png"
          alt="Granddaughter recording her grandmother's family story on a porch"
          className="hero-banner"
          width="1672"
          height="941"
        />
        <Container className="hero-content">
          <Row className="justify-content-center">
            <Col lg={8} className="text-center">
              <h1 className="display-4 mb-3">
                Capture your family's <span className="hero-accent">story and legacy.</span>
              </h1>
              <p className="hero-subtitle mb-4">
                Guided video interviews with your parents and grandparents. Weekly
                video moments across the whole family. Celebrations everyone shows
                up for. Same job as StoryWorth and memoir books — built for how
                families actually want to communicate today.
              </p>

              <div className="d-flex flex-column gap-3">
                <div className="download-cta">
                  <div className="d-flex flex-column flex-sm-row justify-content-center gap-3">
                    <a
                      className="download-badge apple"
                      href={hero.appStore}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => {
                        window.gtag?.('event', 'app_store_click', {
                          event_category: 'Install',
                          event_label: 'Home Hero',
                          transport_type: 'beacon'
                        });
                      }}
                    >
                      <AppleLogo size={36} weight="fill" />
                      <div className="download-badge-text">
                        <small>Download on the</small>
                        <span>App Store</span>
                      </div>
                    </a>
                    <a
                      className="download-badge google"
                      href={hero.playStore}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => {
                        window.gtag?.('event', 'play_store_click', {
                          event_category: 'Install',
                          event_label: 'Home Hero',
                          transport_type: 'beacon'
                        });
                      }}
                    >
                      <GooglePlayLogo size={36} weight="fill" />
                      <div className="download-badge-text">
                        <small>Get it on</small>
                        <span>Google Play</span>
                      </div>
                    </a>
                  </div>
                  <p className="trust-line mt-3 mb-0">
                    Free for 6 months · Then $4.99/month for the whole family · Download your videos anytime
                  </p>
                </div>
              </div>
            </Col>
          </Row>
          <div className="scroll-cue" aria-hidden="true">
            <CaretDown size={28} weight="bold" className="scroll-cue-arrow" />
          </div>
        </Container>
      </section>

      {/* Product reel */}
      <section className="py-5 product-reel-section">
        <Container>
          <Row className="justify-content-center">
            <Col lg={8} className="text-center">
              <h2 className="section-title">See Family Ring in 30 seconds</h2>
              <p className="text-muted mb-4">
                A quick look at how families capture stories, weekly moments, and celebrations together.
              </p>
              <div className="product-reel-frame">
                <video
                  src="/brand/family-ring-intro.mp4"
                  className="product-reel-video"
                  controls
                  playsInline
                  preload="metadata"
                  width="720"
                  height="1564"
                />
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Problem validation */}
      <section className="py-5 problem-section">
        <Container>
          <div className="text-center mb-5">
            <h2 className="section-title">Your family deserves better than a group chat.</h2>
            <p className="text-muted">Most of the tools we use for family weren't built for family.</p>
          </div>
          <Row className="g-4">
            <Col md={6}>
              <div className="problem-item">
                <ChatCircleText size={32} weight="light" />
                <div>
                  <h5>Group chats lose everything</h5>
                  <p className="mb-0 text-muted">
                    Scroll far enough and it's gone. Every meaningful exchange buried
                    under logistics and memes.
                  </p>
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="problem-item">
                <EnvelopeOpen size={32} weight="light" />
                <div>
                  <h5>Your parents have stories you've never heard</h5>
                  <p className="mb-0 text-muted">
                    Not because they don't want to share. Because nobody's asked the
                    right question at the right moment.
                  </p>
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="problem-item">
                <UsersThree size={32} weight="light" />
                <div>
                  <h5>Social media wants your family. Your family deserves better.</h5>
                  <p className="mb-0 text-muted">
                    Public feeds, public comments, algorithms deciding who sees what.
                    None of that is how families actually work.
                  </p>
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="problem-item">
                <Calendar size={32} weight="light" />
                <div>
                  <h5>Celebrations pass without capture</h5>
                  <p className="mb-0 text-muted">
                    Birthdays, anniversaries, holidays — a few photos, then quiet.
                    Nothing to go back to.
                  </p>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="py-5">
        <Container>
          <div className="text-center mb-5">
            <h2 className="section-title">How Family Ring works</h2>
            <p className="text-muted">Three simple ways your family shows up every week.</p>
          </div>
          <Row className="g-4">
            <Col md={4}>
              <div className="feature-card h-100 text-center">
                <div className="mb-3">
                  <VideoCamera size={56} color="var(--brand-accent-mint)" weight="duotone" />
                </div>
                <h5>Weekly Callouts</h5>
                <p className="mb-0">
                  One question. The whole family answers on video. Every week. See
                  who's answered, see what they said, reply with your own.
                </p>
              </div>
            </Col>
            <Col md={4}>
              <div className="feature-card h-100 text-center">
                <div className="mb-3">
                  <ChatCircleText size={56} color="var(--brand-accent-mint)" weight="duotone" />
                </div>
                <h5>Guided Interviews</h5>
                <p className="mb-0">
                  Hundreds of thoughtful prompts to ask the people you love about
                  their lives. Smart follow-up questions help draw out the real
                  stories based on what they just shared.
                </p>
              </div>
            </Col>
            <Col md={4}>
              <div className="feature-card h-100 text-center">
                <div className="mb-3">
                  <ClockCounterClockwise size={56} color="var(--brand-accent-mint)" weight="duotone" />
                </div>
                <h5>Shared Celebrations</h5>
                <p className="mb-0">
                  Birthdays, holidays, milestones — Family Ring prompts the family,
                  collects everyone's messages, and turns them into something worth
                  going back to.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Selfie teaser - sits between How it works and comparison */}
      <section className="py-5 selfie-teaser-section">
        <Container style={{ maxWidth: 960 }}>
          <div className="text-center mb-4">
            <div className="selfie-icon-card">
              <img
                src="/brand/selfie-icon.png"
                alt="Selfie"
                width="120"
                height="120"
              />
            </div>
            <h3 className="fst-italic fw-bold mb-2">Selfie&trade;</h3>
            <h2 className="section-title mt-0">
              The people you love don't have to disappear when they're gone.
            </h2>
          </div>
          <p className="lead">
            Once a family member has recorded a few videos, Selfie designs and develops their private AI  — a chat trained on their actual stories,
            enhanced by your feedback, that anyone in your family ring can talk to. Ask your grandfather what it was
            like growing up in his hometown, even when he's not around to answer.
            Today it's text; voice and video are possible in the future depending on
            what families ask for.
          </p>
          <p className="lead">
            Selfie is yours. It uses only the videos that family member chose to
            record, available only to the family ring they're part of, never trained
            on or available to anything outside your circle.
          </p>
          <div className="text-center mt-4">
            <a href="/selfie" className="btn btn-outline-brand btn-lg">
              Learn more about Selfie &rarr;
            </a>
          </div>
        </Container>
      </section>

      {/* Differentiation / comparison */}
      <section className="py-5 comparison-section">
        <Container>
          <div className="text-center mb-4">
            <h2 className="section-title">Why Family Ring, not everything else</h2>
            <p className="text-muted">
              Group chats, social media, and memoir-by-email services all try to solve
              some part of this. None of them try to solve all of it.
            </p>
          </div>
          <div className="comparison-table-wrap">
            <table className="comparison-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Group Chats</th>
                  <th>StoryWorth</th>
                  <th className="highlight">Family Ring</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th scope="row">Video by default</th>
                  <td>No</td>
                  <td>No (voice on higher tiers)</td>
                  <td className="highlight">Yes</td>
                </tr>
                <tr>
                  <th scope="row">Multi-generational</th>
                  <td>Yes</td>
                  <td>No — one storyteller</td>
                  <td className="highlight">One subscription, up to 10 family members</td>
                </tr>
                <tr>
                  <th scope="row">Private by default</th>
                  <td>Mostly (platform-dependent)</td>
                  <td>Yes</td>
                  <td className="highlight">Yes, invite-only</td>
                </tr>
                <tr>
                  <th scope="row">Designed for ongoing use</th>
                  <td>Daily, chat-focused</td>
                  <td>Fades after 2–3 months</td>
                  <td className="highlight">Weekly, forever</td>
                </tr>
                <tr>
                  <th scope="row">Free trial</th>
                  <td>—</td>
                  <td>None — no trial, no monthly</td>
                  <td className="highlight">6 months free, full features</td>
                </tr>
                <tr>
                  <th scope="row">Starting price</th>
                  <td>Free</td>
                  <td>$59–$199/year</td>
                  <td className="highlight">$4.99/month for the whole family</td>
                </tr>
                <tr>
                  <th scope="row">Your videos if you cancel</th>
                  <td>—</td>
                  <td>Read-only — renewal required</td>
                  <td className="highlight">Download anytime</td>
                </tr>
                <tr>
                  <th scope="row">AI memory of your family members</th>
                  <td>—</td>
                  <td>—</td>
                  <td className="highlight">Selfie&trade; — private AI chat trained on their videos and enhanced by your feedback</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="text-center mt-4">
            <a href="/compare/storyworth" className="btn btn-outline-brand">
              See the full StoryWorth comparison →
            </a>
          </div>
        </Container>
      </section>

      {/* FAQ with FAQPage schema visible */}
      <section className="py-5 faq-section">
        <Container style={{ maxWidth: 820 }}>
          <div className="text-center mb-4">
            <h2 className="section-title">Questions families ask</h2>
          </div>
          <Accordion>
            {faqs.map((item, i) => (
              <Accordion.Item eventKey={String(i)} key={item.q}>
                <Accordion.Header>{item.q}</Accordion.Header>
                <Accordion.Body>{item.a}</Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
          <p className="text-center mt-4">
            <a href="/faq" className="text-decoration-none">See all answers →</a>
          </p>
        </Container>
      </section>

      {/* Closing CTA */}
      <section className="py-5 closing-cta-section">
        <Container className="text-center">
          <h2 className="mb-3">Start your Family Ring.</h2>
          <p className="text-muted mb-4">
            Free for six months. The people you love and their stories are here today.
          </p>
          <div className="d-flex flex-column flex-sm-row justify-content-center gap-3">
            <a
              className="download-badge apple"
              href={closing.appStore}
              target="_blank"
              rel="noreferrer"
              onClick={() => {
                window.gtag?.('event', 'app_store_click', {
                  event_category: 'Install',
                  event_label: 'Home Closing CTA',
                  transport_type: 'beacon'
                });
              }}
            >
              <AppleLogo size={36} weight="fill" />
              <div className="download-badge-text">
                <small>Download on the</small>
                <span>App Store</span>
              </div>
            </a>
            <a
              className="download-badge google"
              href={closing.playStore}
              target="_blank"
              rel="noreferrer"
              onClick={() => {
                window.gtag?.('event', 'play_store_click', {
                  event_category: 'Install',
                  event_label: 'Home Closing CTA',
                  transport_type: 'beacon'
                });
              }}
            >
              <GooglePlayLogo size={36} weight="fill" />
              <div className="download-badge-text">
                <small>Get it on</small>
                <span>Google Play</span>
              </div>
            </a>
          </div>
          <p className="text-muted mt-3 mb-0 small">
            6 months free · Then $4.99/month for the whole family · Cancel anytime
          </p>
        </Container>
      </section>
    </>
  );
}

export default HomePage;
